import {useState, useCallback} from 'react';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';

import config from '../../config/config';
import CustomInput from './CustomInput';

const getIsSelected = ({value, multi}) => optionValue => {
  if (multi) return Array.isArray(value) && value.includes(optionValue);
  return value === optionValue;
};

const handleClick = ({
  value, 
  multi, 
  disabled, 
  isControlled, 
  onChange, 
  setInternalValue
}) => optionValue => {
    if (disabled) return;
    let newValue;
    if (multi) {
      const current = Array.isArray(value) ? value : [];
      newValue = current.includes(optionValue)
        ? current.filter(v => v !== optionValue)
        : [...current, optionValue];
    } else {
      newValue = value === optionValue ? null : optionValue;
    }
    if (!isControlled) setInternalValue(newValue);
    if (onChange) onChange({target: {value: newValue}}, newValue);
  };

const ChoiceField = props => {
  const {
    options = [],
    value: controlledValue,
    defaultValue,
    onChange,
    multi = false,
    disabled = false,
    ...rest
  } = props;

  const isControlled = controlledValue !== undefined;
  const [internalValue, setInternalValue] = useState(
    defaultValue ?? (multi ? [] : null)
  );
  const value = isControlled ? controlledValue : internalValue;

  const isSelected = useCallback(getIsSelected({value, multi}), [value, multi]);

  const onClick = useCallback(handleClick({
    value, 
    multi, 
    disabled, 
    isControlled, 
    onChange, 
    setInternalValue
  }), [value, multi, disabled, isControlled, onChange]);

  return (
    <CustomInput {...rest}>
      <select
        multiple={multi}
        value={multi ? (Array.isArray(value) ? value : []) : (value ?? '')}
        readOnly
        style={{display: 'none'}}
        onChange={() => {}}
      >
        <option value='' />
        {options.map(o => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
      <Stack direction='row' spacing={1} flexWrap='wrap' useFlexGap>
        {options.map(o => {
          const selected = isSelected(o.value);
          return (
            <Box
              key={o.value}
              onClick={() => onClick(o.value)}
              sx={{
                px: 2,
                py: 0.75,
                borderRadius: config().styles.defaultRadius,
                cursor: disabled ? 'default' : 'pointer',
                userSelect: 'none',
                border: selected ? `0.5px solid ${config().colors.primary}` : config().styles.defaultBorder,
                bgcolor: selected ? config().colors.primary : '#fff',
                color: selected ? '#fff' : 'text.primary',
                opacity: disabled ? 0.5 : 1,
                transition: 'all 0.15s ease',
                '&:hover': disabled ? {} : {
                  bgcolor: selected ? config().colors.primary : 'grey.100',
                },
              }}
            >
              {o.label}
            </Box>
          );
        })}
      </Stack>
    </CustomInput>
  );
};

export default ChoiceField;
